import type { AutomationCategoryResponse } from '@reach/shared-types/automation-category';
import type { AutomationTemplate } from '../templates.types';

/**
 * Status filter values for the automation-templates list. `All` is the no-active-filter
 * sentinel; the other three double as the derived per-template status (see TemplateListStatus).
 */
export enum StatusFilter {
  All = 'all',
  Active = 'active',
  Available = 'available',
  Draft = 'draft',
}

/**
 * Derived list-status tier for a template. There is no stored status column — it is computed from
 * the raw `targetedTenantsFilter` and the `activeBusinessIds` the list API returns.
 * - `Draft`: synced to no one (`targetedTenantsFilter === []`)
 * - `Available`: synced to ≥1 tenant, but none has it enabled
 * - `Active`: ≥1 tenant has it enabled
 */
export type TemplateListStatus = StatusFilter.Active | StatusFilter.Available | StatusFilter.Draft;

export const TEMPLATE_STATUS_LABEL: Record<TemplateListStatus, string> = {
  [StatusFilter.Active]: 'Active',
  [StatusFilter.Available]: 'Available',
  [StatusFilter.Draft]: 'Draft',
};

export type TemplateSortKey = 'name' | 'tenantsUsing' | 'status';
export type SortDirection = 'asc' | 'desc';

export interface TemplateListRow {
  template: AutomationTemplate;
  displayName: string;
  description: string;
  status: TemplateListStatus;
  templateSubscriberCount: number;
  activeBusinessIds: string[];
  categories: AutomationCategoryResponse[];
}

export interface TenantFilterOption {
  id: string;
  name: string;
  templateCount: number;
}

/** View model returned by useTemplateListView and consumed by the toolbar and table. */
export interface TemplateListView {
  search: string;
  setSearch: (value: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (value: StatusFilter) => void;
  statusCounts: Record<StatusFilter, number>;
  selectedTenantIds: string[];
  toggleTenant: (tenantId: string) => void;
  clearTenants: () => void;
  tenantFilterOptions: TenantFilterOption[];
  tenantNameById: Map<string, string>;
  categories: AutomationCategoryResponse[];
  selectedCategoryIds: string[];
  toggleCategory: (categoryId: string) => void;
  clearCategories: () => void;
  sortKey: TemplateSortKey;
  sortDirection: SortDirection;
  toggleSort: (key: TemplateSortKey) => void;
  rows: TemplateListRow[];
  totalCount: number;
  filteredCount: number;
  hasActiveFilters: boolean;
  clearAllFilters: () => void;
  isTenantsLoading: boolean;
  isLoading: boolean;
  error: unknown;
}
